import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { ComposableMap, Geographies, Geography, ZoomableGroup, Marker } from "react-simple-maps";
import { geoCentroid, geoEqualEarth } from "d3-geo";
import { ChevronRight, RotateCcw, Loader2, X, ExternalLink, Home, Plus, Minus, MapPinOff, MapPinned, List, ShieldAlert, Ban } from "lucide-react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import UsChoroplethMap from "@/components/UsChoroplethMap";
import BlockIpDialog from "@/components/admin/BlockIpDialog";

const GEO_URL = "/geo/countries-110m.json";
const US_STATES_URL = "/geo/us-states.json";
const LEVEL_ZOOM = { world: 1, country: 4.5, region: 7, city: 10 };
const US_CENTER = [-96.5, 38.7];
const US_OLIVE = "#8B9556";
const NONUS = "#CCFF00";

// SITREP map time windows. Live = currently active (matches the ACTIVE NOW stat + Live Activity
// table). 7D/30D = trailing activity. All-time = cumulative since launch (clearly separate, NOT default).
const WINDOW_OPTIONS = [["live", "Live"], ["7d", "7D"], ["30d", "30D"], ["all", "All-time"]];
const WINDOW_LABEL = {
  live: "Active now · last 10 min",
  "7d": "Active in the last 7 days",
  "30d": "Active in the last 30 days",
  all: "All-time · cumulative since launch",
};
const WINDOW_TITLE = { live: "Live Sessions", "7d": "Sessions · 7D", "30d": "Sessions · 30D", all: "All-Time Sessions" };

// The ComposableMap below uses the react-simple-maps default projection (geoEqualEarth) with
// scale 155, width 980, height 470 → translate [490,235]. We reuse an identical projection here
// to compute each bucket's on-screen pixel position for LABEL DECLUTTERING + auto-fit. The
// ZoomableGroup maps a projected point p to screen: [W/2 + zoom*(p.x-pc.x), H/2 + zoom*(p.y-pc.y)]
// where pc = projection(center) — so these numbers match what the map actually draws.
const MAP_W = 980, MAP_H = 470;
const PROJ = geoEqualEarth().scale(155).translate([MAP_W / 2, MAP_H / 2]);

// US state full-name -> USPS code (matches active_sessions.region codes for choropleth join).
const US_NAME_TO_CODE = {
  Alabama:"AL",Alaska:"AK",Arizona:"AZ",Arkansas:"AR",California:"CA",Colorado:"CO",Connecticut:"CT",
  Delaware:"DE","District of Columbia":"DC",Florida:"FL",Georgia:"GA",Hawaii:"HI",Idaho:"ID",Illinois:"IL",
  Indiana:"IN",Iowa:"IA",Kansas:"KS",Kentucky:"KY",Louisiana:"LA",Maine:"ME",Maryland:"MD",Massachusetts:"MA",
  Michigan:"MI",Minnesota:"MN",Mississippi:"MS",Missouri:"MO",Montana:"MT",Nebraska:"NE",Nevada:"NV",
  "New Hampshire":"NH","New Jersey":"NJ","New Mexico":"NM","New York":"NY","North Carolina":"NC","North Dakota":"ND",
  Ohio:"OH",Oklahoma:"OK",Oregon:"OR",Pennsylvania:"PA","Rhode Island":"RI","South Carolina":"SC","South Dakota":"SD",
  Tennessee:"TN",Texas:"TX",Utah:"UT",Vermont:"VT",Virginia:"VA",Washington:"WA","West Virginia":"WV",
  Wisconsin:"WI",Wyoming:"WY","Puerto Rico":"PR",
};

function fmtDuration(s) {
  if (s == null) return "—";
  if (s < 60) return `${s}s`;
  if (s < 3600) return `${Math.round(s / 60)}m`;
  return `${(s / 3600).toFixed(1)}h`;
}
function fmtTime(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
}

export default function SitrepDrillMap() {
  const [level, setLevel] = useState("world");
  const [path, setPath] = useState({});
  const [center, setCenter] = useState([0, 20]);
  const [zoom, setZoom] = useState(1);
  const [mapKey, setMapKey] = useState(0); // bump to remount ZoomableGroup (zoom buttons / reset / drill)
  const [buckets, setBuckets] = useState([]);
  const [markers, setMarkers] = useState([]);
  const [total, setTotal] = useState(0);
  const [unknownCount, setUnknownCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [geoliteActive, setGeoliteActive] = useState(false);
  const [mode, setMode] = useState("real");   // all | real | exclude_automation (Item 9)
  const [timeWindow, setTimeWindow] = useState("live");   // live | 7d | 30d | all — DEFAULT live (matches ACTIVE NOW)
  const [members, setMembers] = useState(0);
  const [selected, setSelected] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [clusterList, setClusterList] = useState(null);
  const [geoLoadError, setGeoLoadError] = useState(false);
  const [sessionList, setSessionList] = useState(null);
  const [listLoading, setListLoading] = useState(false);
  const [blockTarget, setBlockTarget] = useState(null);
  const mapWrapRef = useRef(null);
  const fitSigRef = useRef("");          // guards region auto-fit from re-running on every render

  // macOS trackpad fix: a two-finger PINCH arrives as a `wheel` event with ctrlKey=true, which
  // react-simple-maps' default d3-zoom filter rejects — so pinch-to-zoom did nothing and the page
  // zoomed instead. A two-finger SCROLL is a wheel event the browser may treat as page scroll. We
  // attach a NON-PASSIVE wheel listener on the map wrapper that preventDefaults EVERY wheel over
  // the map (kills page scroll/browser-zoom), and pass filterZoomEvent below so d3-zoom accepts the
  // pinch and zooms it centered on the cursor (its native wheel behaviour). Every drill level is
  // now zoomable — the world/country/region/city inline map AND the US choropleth (UsChoroplethMap
  // with zoomable=true, which also self-captures wheel) — so we always capture, covering the small
  // margins around the choropleth too. Without this the US choropleth level scrolled the page.
  useEffect(() => {
    const el = mapWrapRef.current;
    if (!el) return;
    const onWheel = (e) => e.preventDefault();
    el.addEventListener("wheel", onWheel, { passive: false });
    return () => el.removeEventListener("wheel", onWheel);
  }, []);

  // TOUCH pinch fix (item): the inline world/country/region/city ComposableMap (unlike the US
  // choropleth, which manages this itself) needs cooperative touch-action — 1 finger lets the PAGE
  // scroll (touch-action:auto), 2+ fingers hand the pan+pinch to d3-zoom (touch-action:none).
  // Without this the browser swallowed the two-finger pinch and the map never zoomed on touch.
  useEffect(() => {
    const el = mapWrapRef.current;
    if (!el) return;
    const applyTA = (n) => { el.style.touchAction = n >= 2 ? "none" : "auto"; };
    const onStart = (e) => applyTA(e.touches.length);
    const onEnd = (e) => applyTA(e.touches.length);
    el.style.touchAction = "auto";
    el.addEventListener("touchstart", onStart, { passive: true });
    el.addEventListener("touchend", onEnd, { passive: true });
    el.addEventListener("touchcancel", onEnd, { passive: true });
    return () => {
      el.removeEventListener("touchstart", onStart);
      el.removeEventListener("touchend", onEnd);
      el.removeEventListener("touchcancel", onEnd);
    };
  }, []);

  const loadLevel = useCallback(async (lvl, p, md, win) => {
    setLoading(true); setSelected(null); setClusterList(null); setSessionList(null);
    try {
      if (lvl === "city") {
        const { data } = await api.get(`/admin/sitrep/markers`, { params: { country: p.country, region: p.region, city: p.city, mode: md, window: win } });
        const mk = data.markers || [];
        setMarkers(mk); setBuckets([]); setTotal(data.count || 0);
        setMembers(new Set(mk.map((m) => m.user_id).filter(Boolean)).size);
      } else {
        const params = { level: lvl, mode: md, window: win };
        if (p.country) params.country = p.country;
        if (p.region) params.region = p.region;
        const { data } = await api.get(`/admin/sitrep/geo`, { params });
        setBuckets(data.buckets || []); setMarkers([]); setTotal(data.total || 0);
        setMembers(data.members || 0);
        setUnknownCount(data.unknown_count || 0);
        setGeoliteActive(!!data.geolite2_active);
      }
    } catch (e) { /* handled by empty state */ } finally { setLoading(false); }
  }, []);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadLevel(level, path, mode, timeWindow); }, [mode, timeWindow]);
  useEffect(() => { loadLevel("world", {}, "real", "live"); }, [loadLevel]);

  const descend = (b) => {
    if (b == null || b.lat == null || b.lon == null || b.key === "unknown" || !b.count) return;
    let nl, np;
    if (level === "world") { nl = "country"; np = { country: b.key, countryName: b.name }; }
    else if (level === "country") { nl = "region"; np = { ...path, region: b.key, regionName: b.name }; }
    else if (level === "region") { nl = "city"; np = { ...path, city: b.key }; }
    else return;
    const isUS = nl === "country" && String(np.country).toUpperCase() === "US";
    setLevel(nl); setPath(np);
    setCenter(isUS ? US_CENTER : [b.lon, b.lat]);
    setZoom(isUS ? 1 : LEVEL_ZOOM[nl]);
    fitSigRef.current = "";   // allow the region auto-fit to run for the freshly-loaded scope
    setMapKey((k) => k + 1); loadLevel(nl, np, mode, timeWindow);
  };

  const goTo = (lvl) => {
    let np = {};
    if (lvl === "country") np = { country: path.country, countryName: path.countryName };
    else if (lvl === "region") np = { country: path.country, countryName: path.countryName, region: path.region, regionName: path.regionName };
    const isUS = lvl === "country" && String(np.country).toUpperCase() === "US";
    setLevel(lvl); setPath(np); setZoom(isUS ? 1 : LEVEL_ZOOM[lvl]);
    if (lvl === "world") setCenter([0, 20]); else if (isUS) setCenter(US_CENTER);
    fitSigRef.current = "";
    setMapKey((k) => k + 1); loadLevel(lvl, np, mode, timeWindow);
  };

  const reset = () => { setLevel("world"); setPath({}); setCenter([0, 20]); setZoom(1); fitSigRef.current = ""; setMapKey((k) => k + 1); loadLevel("world", {}, mode, timeWindow); };
  const nudgeZoom = (factor) => setZoom((z) => { const nz = Math.max(1, Math.min(24, z * factor)); setMapKey((k) => k + 1); return nz; });

  const openDetail = async (sessionId) => {
    setDetailLoading(true); setSelected({ session_id: sessionId }); setClusterList(null);
    try { const { data } = await api.get(`/admin/sitrep/session/${sessionId}`); setSelected(data); }
    catch (e) { setSelected({ error: true, session_id: sessionId }); }
    finally { setDetailLoading(false); }
  };

  // Identity drill-down: reveal the SESSIONS (account + IP + UA + first/last-seen) for
  // the current scope. This is an audited reveal on the server (region counts are not).
  const listSessions = async () => {
    setListLoading(true); setSessionList([]);
    try {
      const params = { country: path.country, mode, window: timeWindow };
      if (path.region) params.region = path.region;
      if (path.city) params.city = path.city;
      const { data } = await api.get(`/admin/sitrep/region-sessions`, { params });
      setSessionList(data.sessions || []);
    } catch (e) { setSessionList([]); }
    finally { setListLoading(false); }
  };

  // City-level clustering by rounded coords (~1km).
  const clusters = useMemo(() => {
    const m = {};
    for (const mk of markers) {
      if (mk.lat == null || mk.lon == null) continue;
      const key = `${mk.lat.toFixed(2)},${mk.lon.toFixed(2)}`;
      (m[key] ||= { lat: mk.lat, lon: mk.lon, items: [], acc: 0 }).items.push(mk);
      m[key].acc = Math.max(m[key].acc, mk.accuracy_radius || 20);
    }
    return Object.values(m);
  }, [markers]);

  // AUTO-FIT (region level): when we descend into a state/province, the fixed zoom (7) can leave
  // several nearby cities piled on ~the same pixel. Fit the view to the city bounding box so the
  // pins actually spread across the viewport. Runs once per freshly-loaded scope (fitSigRef guard),
  // never fighting a subsequent manual pan/zoom.
  useEffect(() => {
    if (level !== "region") return;
    const pts = buckets.filter((b) => b.lat != null && b.lon != null && b.count);
    if (pts.length === 0) return;
    const sig = `${path.country}|${path.region}|${pts.length}`;
    if (fitSigRef.current === sig) return;
    fitSigRef.current = sig;
    const lons = pts.map((p) => p.lon), lats = pts.map((p) => p.lat);
    const minLon = Math.min(...lons), maxLon = Math.max(...lons);
    const minLat = Math.min(...lats), maxLat = Math.max(...lats);
    const p1 = PROJ([minLon, maxLat]) || [0, 0];
    const p2 = PROJ([maxLon, minLat]) || [0, 0];
    const dx = Math.abs(p2[0] - p1[0]);
    const dy = Math.abs(p2[1] - p1[1]);
    let z;
    if (dx < 1.5 && dy < 1.5) z = 9;   // one city (or a tight cluster) — a sensible close-in zoom
    else z = Math.min((MAP_W * 0.5) / Math.max(dx, 1), (MAP_H * 0.5) / Math.max(dy, 1));
    z = Math.max(6, Math.min(20, z));
    setCenter([(minLon + maxLon) / 2, (minLat + maxLat) / 2]);
    setZoom(z);
    setMapKey((k) => k + 1);
  }, [level, buckets, path.country, path.region]);

  // MARKER LAYOUT (non-city buckets) — computed in the SVG's ROOT viewBox space (0..980 × 0..470),
  // NOT inside the zoomed group. Two reasons: (1) SVG <text> whose font-size is divided by a large
  // zoom collapses to ZERO glyph width (sub-pixel), which made high-zoom city labels invisible —
  // rendering in root space lets us use a normal font-size; (2) within a metro (Chicago + Morton
  // Grove + Evanston…) the pins project to nearly the SAME pixel and even max zoom can't separate
  // ~15km, so we SPIDERFY a colliding group into a tidy column with a leader line back to the true
  // location. Coords match the base map because we apply the SAME transform the ZoomableGroup uses:
  // screen = W/2 + zoom*(proj(p) - proj(center)). Recomputed whenever center/zoom change.
  const markerItems = useMemo(() => {
    if (level === "city") return [];
    const pc = PROJ(center) || [MAP_W / 2, MAP_H / 2];
    const items = buckets
      .filter((b) => b.lat != null && b.lon != null && b.count)
      .map((b) => {
        const p = PROJ([b.lon, b.lat]) || [0, 0];
        const r = Math.min(22, 5 + Math.sqrt(b.count) * 2.2);
        const color = b.is_us || level !== "world" ? US_OLIVE : NONUS;
        return { key: b.key, b, count: b.count, r, color,
          sx: MAP_W / 2 + zoom * (p[0] - pc[0]), sy: MAP_H / 2 + zoom * (p[1] - pc[1]) };
      });
    const COLLIDE = 26, ROW_H = 20;
    const groups = [];
    for (const it of items) {
      let g = groups.find((gr) => Math.hypot(gr.cx - it.sx, gr.cy - it.sy) < COLLIDE);
      if (!g) { g = { members: [], cx: it.sx, cy: it.sy }; groups.push(g); }
      g.members.push(it);
      g.cx = g.members.reduce((s, m) => s + m.sx, 0) / g.members.length;
      g.cy = g.members.reduce((s, m) => s + m.sy, 0) / g.members.length;
    }
    const laid = [];
    for (const g of groups) {
      if (g.members.length === 1) {
        const m = g.members[0];
        laid.push({ ...m, dx: m.sx, dy: m.sy, anchor: "above", clustered: false });
      } else {
        const ms = g.members.slice().sort((a, b) => b.count - a.count);
        const n = ms.length;
        const colX = g.cx + 14;
        const startY = g.cy - ((n - 1) * ROW_H) / 2;
        ms.forEach((m, i) => {
          laid.push({ ...m, dx: colX, dy: startY + i * ROW_H, anchor: "right", clustered: true });
        });
      }
    }
    return laid;
  }, [buckets, center, zoom, level]);

  // USA choropleth: state-code -> {count, bucket} + max, for the shaded fill.
  const usIsChoropleth = level === "country" && (path.country || "").toUpperCase() === "US";
  const stateByCode = useMemo(() => {
    const m = {}; for (const b of buckets) m[(b.key || "").toUpperCase()] = b; return m;
  }, [buckets]);
  const maxCount = useMemo(() => buckets.reduce((mx, b) => Math.max(mx, b.count || 0), 0) || 1, [buckets]);
  const shade = (count) => {
    if (!count) return "#22271D";
    const t = Math.min(1, 0.22 + (count / maxCount) * 0.78);
    return `rgba(160,180,90,${t.toFixed(2)})`;
  };
  // Sessions whose COUNTRY resolved but REGION did not (datacenter / unmapped IPs) land in an
  // "unknown" bucket that can't shade any state — surface its count so the map is never "empty".
  const unknownBucket = useMemo(
    () => buckets.find((b) => String(b.key).toLowerCase() === "unknown" && b.count), [buckets]);
  // Never render blank: if the state geometry file fails to load, show an explicit error.
  useEffect(() => {
    if (!(level === "country" && (path.country || "").toUpperCase() === "US")) { setGeoLoadError(false); return; }
    let cancelled = false;
    fetch(US_STATES_URL)
      .then((r) => { if (!r.ok) throw new Error("http " + r.status); return r.json(); })
      .then((j) => { if (!cancelled) setGeoLoadError(!(j && Array.isArray(j.features) && j.features.length > 10)); })
      .catch(() => { if (!cancelled) setGeoLoadError(true); });
    return () => { cancelled = true; };
  }, [level, path.country]);

  const crumbs = [{ lvl: "world", label: "World" }];
  if (path.countryName) crumbs.push({ lvl: "country", label: path.countryName });
  if (path.regionName) crumbs.push({ lvl: "region", label: path.regionName });
  if (path.city) crumbs.push({ lvl: "city", label: path.city });

  const btn = "border border-sniper px-2 py-1 hover:border-white/40 text-foreground inline-flex items-center gap-1 bg-black/70";

  return (
    <div className="border border-sniper bg-surface mt-6" data-testid="sitrep-map">
      <div className="px-4 pt-3 pb-1 flex items-center gap-2 flex-wrap">
        <MapPinned className="w-4 h-4 text-olive-bright" />
        <h2 className="font-heading text-lg uppercase tracking-tight text-foreground" data-testid="sitrep-map-title">{WINDOW_TITLE[timeWindow] || "Sessions"}</h2>
        {timeWindow === "live" && (
          <span className="relative flex h-2 w-2" title="Live" data-testid="sitrep-map-live-dot">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-volt opacity-75" />
            <span className="relative inline-flex rounded-full h-2 w-2 bg-volt" />
          </span>
        )}
        <span className={`font-mono text-[10px] uppercase px-1.5 py-0.5 border ${timeWindow === "all" ? "text-amber-400 border-amber-400/40" : "text-olive-bright border-olive/40"}`} data-testid="sitrep-map-window-label">
          {WINDOW_LABEL[timeWindow]}
        </span>
        <span className="font-mono text-[10px] uppercase text-muted-foreground/70" data-testid="sitrep-map-subtitle">· {total} session{total === 1 ? "" : "s"} · {members} member{members === 1 ? "" : "s"}</span>
      </div>
      <div className="px-4 py-3 border-b border-sniper flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-1.5 flex-wrap font-mono text-[11px] uppercase" data-testid="sitrep-breadcrumb">
          {crumbs.map((c, i) => (
            <span key={c.lvl} className="flex items-center gap-1.5">
              {i > 0 && <ChevronRight className="w-3 h-3 text-muted-foreground" />}
              <button onClick={() => c.lvl !== level && goTo(c.lvl)} data-testid={`sitrep-crumb-${c.lvl}`}
                className={`inline-flex items-center gap-1 ${c.lvl === level ? "text-olive-bright" : "text-muted-foreground hover:text-foreground"}`}>
                {c.lvl === "world" && <Home className="w-3 h-3" />}{c.label}
              </button>
            </span>
          ))}
          <span className="text-muted-foreground/60 ml-1" data-testid="sitrep-session-summary">· {total} session{total === 1 ? "" : "s"} · {members} member{members === 1 ? "" : "s"}</span>
          {loading && <Loader2 className="w-3 h-3 animate-spin text-olive-bright ml-1" />}
        </div>
        <div className="flex items-center gap-3 font-mono text-[10px] uppercase text-muted-foreground">
          {/* TIME WINDOW — Live (matches ACTIVE NOW) / 7D / 30D / All-time (cumulative since launch). */}
          <div className="inline-flex border border-sniper" data-testid="sitrep-window-filter" title="Live = active right now · All-time = every session since launch">
            {WINDOW_OPTIONS.map(([id, label]) => (
              <button key={id} onClick={() => setTimeWindow(id)} data-testid={`sitrep-window-${id}`}
                className={`px-2 py-0.5 border-r border-sniper last:border-r-0 transition-colors ${timeWindow === id ? (id === "all" ? "text-amber-300 bg-amber-400/15 border-amber-400/40" : "text-foreground bg-olive/20 border-olive/40") : "hover:text-foreground"}`}>
                {label}
              </button>
            ))}
          </div>
          {level !== "world" && path.country && (
            <button onClick={listSessions} disabled={listLoading} data-testid="sitrep-list-sessions"
              className="inline-flex items-center gap-1 px-2 py-0.5 border border-olive/50 text-olive-bright hover:bg-olive/10 transition-colors disabled:opacity-50">
              {listLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : <List className="w-3 h-3" />} List sessions
            </button>
          )}
          {unknownCount > 0 && (
            <span className="inline-flex items-center gap-1 text-amber-400/90 border border-amber-400/30 px-2 py-0.5" data-testid="sitrep-unknown-count">
              <MapPinOff className="w-3 h-3" /> {unknownCount} unknown location{unknownCount === 1 ? "" : "s"}
            </span>
          )}
          {/* Honest SESSIONS filter (Item 9): Real hides QA/test accounts + bot/script UAs;
              Exclude automation keeps demo but still drops bots; All shows everything. */}
          <div className="inline-flex border border-sniper" data-testid="sitrep-mode-filter" title="Automation = QA/test accounts or bot / script / headless sessions">
            {[["all", "All"], ["real", "Real"], ["exclude_automation", "Exclude automation"]].map(([id, label]) => (
              <button key={id} onClick={() => setMode(id)} data-testid={`sitrep-mode-${id}`}
                className={`px-2 py-0.5 border-r border-sniper last:border-r-0 transition-colors ${mode === id ? "text-foreground bg-olive/20 border-olive/40" : "hover:text-foreground"}`}>
                {label}
              </button>
            ))}
          </div>
          {!geoliteActive && (
            <span className="text-muted-foreground/60" data-testid="sitrep-geolite-status">GeoLite2 not loaded · fallback geo</span>
          )}
        </div>
      </div>

      <div className="relative" ref={mapWrapRef}>
        {geoLoadError && (
          <div className="absolute inset-x-3 top-3 z-30 bg-blaze/15 border border-blaze/50 text-blaze font-mono text-[11px] uppercase px-3 py-2" data-testid="sitrep-geo-error">
            Map geometry failed to load — /geo/us-states.json is missing or invalid in this build.
          </div>
        )}
        {(usIsChoropleth || level === "region") && unknownBucket && (
          <div className="absolute bottom-3 left-3 z-20 bg-black/85 border border-amber-400/40 text-amber-300 font-mono text-[10px] uppercase px-2.5 py-1.5 inline-flex items-center gap-1.5" data-testid="sitrep-region-unknown">
            <MapPinOff className="w-3 h-3" /> {unknownBucket.count} session{unknownBucket.count === 1 ? "" : "s"} · region unknown
          </div>
        )}
        {/* Zoom / reset controls for the ZOOMABLE (world / non-US country / region / city) map.
            The US choropleth uses UsChoroplethMap's OWN built-in controls (below), so we hide these
            there to avoid two stacked control clusters. Back-to-World is always in the breadcrumb. */}
        {!usIsChoropleth && (
          <div className="absolute top-3 right-3 z-20 flex flex-col gap-1" data-testid="sitrep-zoom-controls">
            <button onClick={() => nudgeZoom(1.6)} data-testid="sitrep-zoom-in" className={btn} title="Zoom in"><Plus className="w-3.5 h-3.5" /></button>
            <button onClick={() => nudgeZoom(1 / 1.6)} data-testid="sitrep-zoom-out" className={btn} title="Zoom out"><Minus className="w-3.5 h-3.5" /></button>
            <button onClick={reset} data-testid="sitrep-map-reset" className={btn} title="Reset to world"><RotateCcw className="w-3.5 h-3.5" /></button>
          </div>
        )}

        {usIsChoropleth ? (
          /* Shared geoAlbersUsa choropleth (UsChoroplethMap) — auto-fits the country, insets AK/HI,
             and (unlike the world projection) never blows Alaska across the antimeridian. Now
             zoomable so scroll / pinch / +/- work at the US state level too. */
          <UsChoroplethMap
            svgTestId="sitrep-map-svg"
            zoomable={true}
            labelColor="#fff"
            datumForCode={(code) => stateByCode[code] || null}
            labelFor={(b) => (b?.count ? String(b.count) : null)}
            onSelect={(code, b) => descend(b)}
            stateTestId={(code, name) => `sitrep-state-${code || name}`}
            styleFor={(geo, code, b) => {
              const count = b?.count || 0;
              return {
                default: { fill: shade(count), stroke: "#8DA063", strokeWidth: 0.7, outline: "none", cursor: count ? "pointer" : "default" },
                hover: { fill: count ? "rgba(204,255,0,0.6)" : "#2E3626", stroke: "#CCFF00", strokeWidth: 1, outline: "none" },
                pressed: { outline: "none" },
              };
            }}
          />
        ) : (
          <ComposableMap projectionConfig={{ scale: 155 }} width={980} height={470} style={{ width: "100%", height: "auto" }} data-testid="sitrep-map-svg">
            <ZoomableGroup key={`${level}-${path.country || ""}-${path.region || ""}-${path.city || ""}-${mapKey}`}
              center={center} zoom={zoom} minZoom={1} maxZoom={24}
              filterZoomEvent={(e) => !e.button}
              onMoveEnd={({ coordinates, zoom: z }) => { if (coordinates) setCenter(coordinates); if (z) setZoom(z); }}>
              {/* Base world layer */}
              <Geographies geography={GEO_URL}>
                {({ geographies }) => geographies.map((geo) => (
                  <Geography key={geo.rsmKey} geography={geo}
                    style={{ default: { fill: "#181B18", stroke: "#2A2E2A", strokeWidth: 0.4, outline: "none" }, hover: { fill: "#20241E", outline: "none" }, pressed: { outline: "none" } }} />
                ))}
              </Geographies>

              {/* City-level individual/clustered session markers with accuracy radius */}
              {level === "city" && clusters.map((cl, i) => {
                const single = cl.items.length === 1;
                const accR = Math.max(4, (cl.acc || 20) / 6) / zoom;
                return (
                  <Marker key={i} coordinates={[cl.lon, cl.lat]}
                    onClick={() => single ? openDetail(cl.items[0].session_id) : setClusterList(cl.items)}
                    data-testid={single ? `sitrep-marker-${cl.items[0].session_id}` : `sitrep-cluster-${i}`}
                    style={{ default: { cursor: "pointer" } }}>
                    <circle r={accR} fill={US_OLIVE} fillOpacity={0.12} stroke={US_OLIVE} strokeOpacity={0.4} strokeWidth={0.8 / zoom} />
                    <circle r={5 / zoom} fill={cl.items[0].is_vpn ? "#FF5E00" : US_OLIVE} stroke="#0A0A0A" strokeWidth={0.6 / zoom} />
                    {!single && <text textAnchor="middle" dy={3.5 / zoom} fill="#0A0A0A" fontSize={9 / zoom} fontWeight="bold" fontFamily="monospace">{cl.items.length}</text>}
                  </Marker>
                );
              })}
            </ZoomableGroup>

            {/* Aggregate bucket markers (world / non-US country / region) rendered in ROOT viewBox
                space (not zoom-scaled) so labels use a normal font size and colliding metros are
                spiderfied into a readable column. Positions track the base map because they use the
                same transform as the ZoomableGroup (recomputed on center/zoom change / move-end). */}
            {level !== "city" && (
              <g data-testid="sitrep-marker-overlay">
                {markerItems.map((it) => {
                  const lblStyle = { paintOrder: "stroke", stroke: "#0A0A0A", strokeWidth: 3, strokeLinejoin: "round", pointerEvents: "none" };
                  const tx = it.anchor === "right" ? it.dx + (it.clustered ? 9 : it.r + 5) : it.dx;
                  const ty = it.anchor === "right" ? it.dy + 4 : it.dy - (it.r + 6);
                  const labelText = `${it.b.name} · ${it.b.count}`;
                  return (
                    <g key={it.key} onClick={() => descend(it.b)} style={{ cursor: "pointer" }} data-testid={`sitrep-bucket-${it.key}`}>
                      {it.clustered && (
                        <>
                          <line x1={it.sx} y1={it.sy} x2={it.dx} y2={it.dy} stroke="#fff" strokeOpacity={0.28} strokeWidth={0.8} />
                          <circle cx={it.sx} cy={it.sy} r={2} fill="#fff" fillOpacity={0.5} />
                          <circle cx={it.dx} cy={it.dy} r={5} fill={it.color} stroke="#0A0A0A" strokeWidth={0.8} />
                        </>
                      )}
                      {!it.clustered && (
                        <>
                          <circle cx={it.dx} cy={it.dy} r={it.r} fill={it.color} fillOpacity={0.28} stroke={it.color} strokeWidth={1} />
                          <circle cx={it.dx} cy={it.dy} r={3} fill={it.color} />
                        </>
                      )}
                      <text x={tx} y={ty} textAnchor={it.anchor === "right" ? "start" : "middle"}
                        fill="#fff" fontSize={11} fontFamily="monospace" style={lblStyle}>{labelText}</text>
                    </g>
                  );
                })}
              </g>
            )}
          </ComposableMap>
        )}

        {!loading && level !== "city" && buckets.length === 0 && (
          <p className="text-center text-xs font-mono uppercase text-muted-foreground py-4" data-testid="sitrep-map-empty">
            {timeWindow === "live" ? "No active sessions right now." : `No geolocated sessions in this window at this level.`}
          </p>
        )}
        {!loading && level === "city" && markers.length === 0 && (
          <p className="text-center text-xs font-mono uppercase text-muted-foreground py-4" data-testid="sitrep-map-empty">No precise sessions here (older than retention, or none).</p>
        )}
        {level !== "city" && !loading && buckets.length > 0 && (
          <p className="text-center text-[10px] font-mono uppercase text-muted-foreground/70 py-2">
            {usIsChoropleth ? "Click a state to descend" : "Click a region to descend"} · scroll / pinch / double-click to zoom · drag to pan
          </p>
        )}

        {/* cluster picker */}
        {clusterList && (
          <div className="absolute top-3 left-3 w-64 bg-black/90 border border-sniper p-3 z-10" data-testid="sitrep-cluster-list">
            <div className="flex items-center justify-between mb-2"><span className="font-mono text-[10px] uppercase text-muted-foreground">{clusterList.length} sessions here</span>
              <button onClick={() => setClusterList(null)}><X className="w-3.5 h-3.5 text-muted-foreground" /></button></div>
            {clusterList.map((m) => (
              <button key={m.session_id} onClick={() => openDetail(m.session_id)} data-testid={`sitrep-marker-${m.session_id}`}
                className="w-full text-left font-mono text-xs text-foreground hover:text-olive-bright py-1 border-b border-sniper/50">@{m.username || "?"} · {m.city}</button>
            ))}
          </div>
        )}

        {/* IP detail panel */}
        {selected && (
          <div className="absolute top-3 left-3 w-[320px] max-w-[92%] bg-black/95 border border-olive/40 p-4 z-10 font-mono text-xs" data-testid="sitrep-detail-panel">
            <div className="flex items-center justify-between mb-2">
              <span className="font-heading text-sm uppercase text-olive-bright">Session / IP detail</span>
              <button onClick={() => setSelected(null)} data-testid="sitrep-detail-close"><X className="w-4 h-4 text-muted-foreground hover:text-foreground" /></button>
            </div>
            {detailLoading ? (
              <div className="flex items-center gap-2 text-muted-foreground py-4"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
            ) : selected.error ? (
              <p className="text-blaze py-2">Could not load session detail.</p>
            ) : selected.retention_expired ? (
              <div data-testid="sitrep-detail-retention">
                <DetailRow k="User" v={<>@{selected.username || "?"} <Standing s={selected.standing} /></>} />
                <DetailRow k="Session" v={`${fmtTime(selected.session_start)} → ${fmtTime(selected.last_seen)}`} />
                <div className="mt-3 border border-amber-400/40 bg-amber-400/10 text-amber-300 p-2.5 leading-snug" data-testid="sitrep-retention-message">
                  {selected.message}
                </div>
              </div>
            ) : (() => {
              const gi = selected.geoip_configured;
              const adv = (v) => (v != null && v !== "" ? v : (gi ? "—" : "GeoIP not configured"));
              return (
              <div className="space-y-1">
                <DetailRow k="User" v={<>@{selected.username || "?"} <Standing s={selected.standing} /> <span className="text-muted-foreground">({selected.role})</span></>} />
                <DetailRow k="IP" v={selected.ip} />
                <DetailRow k="Location" v={`${selected.city || "?"}, ${selected.region || selected.region_name || "?"}, ${selected.country_code || "?"}`} />
                <DetailRow k="Postal" v={adv(selected.postal_code)} />
                <DetailRow k="Coords" v={selected.lat != null ? `${selected.lat.toFixed(4)}, ${selected.lon.toFixed(4)}` : "—"} />
                <DetailRow k="Timezone" v={adv(selected.timezone)} />
                <DetailRow k="Accuracy" v={selected.accuracy_radius != null ? `~${selected.accuracy_radius} km radius` : "—"} />
                <DetailRow k="ASN" v={selected.asn || "—"} />
                <DetailRow k="ISP / Org" v={selected.isp || selected.org || "—"} />
                <DetailRow k="Connection" v={adv(selected.connection_type)} />
                <DetailRow k="Proxy / VPN" v={selected.is_vpn == null ? "detection off" : (selected.is_vpn ? "flagged" : "no")} />
                <DetailRow k="Hosting / DC" v={selected.is_hosting == null ? adv(null) : (selected.is_hosting ? "yes" : "no")} />
                <DetailRow k="Started" v={fmtTime(selected.session_start)} />
                <DetailRow k="Last seen" v={fmtTime(selected.last_seen)} />
                <DetailRow k="Duration" v={fmtDuration(selected.duration_seconds)} />
                <DetailRow k="Device" v={<span className="break-all text-[10px]">{selected.user_agent || "—"}</span>} />
                <div className="pt-2 mt-1 border-t border-sniper flex flex-col gap-1">
                  <Link to={`/admin/accounts?search=${encodeURIComponent(selected.username || "")}`} data-testid="sitrep-link-accounts" className="inline-flex items-center gap-1 text-olive-bright hover:text-volt">
                    <ExternalLink className="w-3 h-3" /> Open in Users console
                  </Link>
                  <button onClick={() => setBlockTarget({ ip: selected.ip, userId: selected.user_id, username: selected.username })}
                    data-testid="sitrep-detail-block" className="inline-flex items-center gap-1 text-blaze hover:text-red-400 mt-0.5">
                    <Ban className="w-3 h-3" /> Block IP / Ban account
                  </button>
                </div>
              </div>
              );
            })()}
          </div>
        )}
      </div>

      {/* Identity session list for the current scope — audited reveal (account + IP + UA) */}
      {sessionList && (
        <div className="border-t border-sniper" data-testid="sitrep-session-list">
          <div className="px-4 py-2.5 flex items-center justify-between border-b border-sniper">
            <span className="font-mono text-[11px] uppercase text-olive-bright inline-flex items-center gap-1.5">
              <ShieldAlert className="w-3.5 h-3.5" /> {sessionList.length} session{sessionList.length === 1 ? "" : "s"} · {[path.city, path.regionName || path.region, path.countryName || path.country].filter(Boolean).join(", ")}
              <span className="text-muted-foreground/60 normal-case">· identity reveal audited</span>
            </span>
            <button onClick={() => setSessionList(null)} data-testid="sitrep-session-list-close"><X className="w-4 h-4 text-muted-foreground hover:text-foreground" /></button>
          </div>
          {sessionList.length === 0 ? (
            <p className="px-4 py-4 font-mono text-xs uppercase text-muted-foreground" data-testid="sitrep-session-list-empty">No sessions in this scope.</p>
          ) : (
            <div className="overflow-x-auto max-h-[360px]">
              <table className="w-full font-mono text-xs">
                <thead>
                  <tr className="text-left text-muted-foreground uppercase text-[10px] border-b border-sniper sticky top-0 bg-surface">
                    <th className="px-3 py-2">Account</th>
                    <th className="px-3 py-2">IP</th>
                    <th className="px-3 py-2">Device</th>
                    <th className="px-3 py-2">First seen</th>
                    <th className="px-3 py-2">Last seen</th>
                    <th className="px-3 py-2 text-right">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-sniper">
                  {sessionList.map((s) => (
                    <tr key={s.session_id} onClick={() => openDetail(s.session_id)} data-testid={`sitrep-session-row-${s.session_id}`}
                      className="text-secondary-foreground hover:bg-olive/5 cursor-pointer">
                      <td className="px-3 py-2 text-foreground">
                        {s.username === "anonymous" ? <span className="text-muted-foreground italic">anonymous</span> : <>@{s.username}</>}
                        <Standing s={s.standing} />
                        {s.is_vpn && <span className="ml-1.5 text-[9px] uppercase text-blaze border border-blaze/50 px-1">VPN</span>}
                      </td>
                      <td className="px-3 py-2 text-muted-foreground">{s.ip_expired ? <span className="italic">expired</span> : (s.ip || "—")}</td>
                      <td className="px-3 py-2 text-muted-foreground max-w-[280px] truncate">{s.user_agent || "—"}</td>
                      <td className="px-3 py-2 text-muted-foreground">{fmtTime(s.first_seen)}</td>
                      <td className="px-3 py-2 text-muted-foreground">{fmtTime(s.last_seen)}</td>
                      <td className="px-3 py-2 text-right">
                        <button
                          onClick={(e) => { e.stopPropagation(); setBlockTarget({ ip: s.ip, ipExpired: s.ip_expired, userId: s.user_id, username: s.username === "anonymous" ? null : s.username }); }}
                          data-testid={`sitrep-session-block-${s.session_id}`}
                          className="inline-flex items-center gap-1 border border-blaze/50 text-blaze hover:bg-blaze/10 font-mono uppercase text-[10px] px-2 py-1 transition-colors">
                          <Ban className="w-3 h-3" /> Block
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
      {blockTarget && (
        <BlockIpDialog target={blockTarget}
          onClose={() => setBlockTarget(null)}
          onDone={() => { setBlockTarget(null); setSelected(null); }} />
      )}
    </div>
  );
}

function DetailRow({ k, v }) {
  return (
    <div className="flex gap-2">
      <span className="text-muted-foreground uppercase text-[10px] w-[80px] shrink-0 pt-0.5">{k}</span>
      <span className="text-foreground break-words">{v}</span>
    </div>
  );
}
function Standing({ s }) {
  if (!s || s === "active") return <span className="text-olive-bright text-[9px] uppercase">active</span>;
  return <span className="text-blaze text-[9px] uppercase border border-blaze/50 px-1">{s}</span>;
}
